import React, { useState, useEffect } from "react";
import { Row, Col, Form, Button, InputGroup, Spinner, Badge, Modal, Toast, ToastContainer } from "react-bootstrap";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { useAuth } from "../hooks/useAuth";

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export default function RegenerateToken() {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [tokenExpiresAt, setTokenExpiresAt] = useState("");
  const [newTokenGenerated, setNewTokenGenerated] = useState(false);
  const [showTokenVisible, setShowTokenVisible] = useState(false);
  const { user } = useAuth();

  // ============================================
  // CHARGEMENT INITIAL
  // ============================================
  useEffect(() => {
    const loadTokenInfo = () => {
      const expiresAt = localStorage.getItem('tokenExpiresAt');
      if (expiresAt) setTokenExpiresAt(expiresAt);
      setLoading(false);
    };
    setTimeout(loadTokenInfo, 800);
  }, []);

  // ============================================
  // GÉNÉRATION DU TOKEN
  // ============================================
  const generateToken = async (e) => {
    if (e) e.preventDefault();

    if (!password) {
      setError("Veuillez saisir votre mot de passe");
      return;
    }

    setRegenerating(true);
    setError("");

    try {
      const sessionToken = localStorage.getItem('authToken');
      if (!sessionToken) {
        throw new Error("Aucune session active. Veuillez vous reconnecter.");
      }

      const response = await fetch(`${API_BASE_URL}/generate-external-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          email: user?.business_email || "",
          password: password
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erreur ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      if (data.token) {
        setToken(data.token);
        setTokenExpiresAt(data.expires_at);
        setNewTokenGenerated(true);
        setShowTokenVisible(true);
        localStorage.setItem('tokenExpiresAt', data.expires_at);
        setPassword("");
        setShowPassword(false);
        setShowGenerateModal(false);
      } else {
        setError(data.message || "Aucun token reçu dans la réponse");
      }
    } catch (err) {
      console.error('Erreur génération:', err);
      setError(err.message || "Erreur de connexion. Veuillez réessayer.");
    } finally {
      setRegenerating(false);
    }
  };

  // ============================================
  // COPIE DU TOKEN
  // ============================================
  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(token);
      setShowCopyToast(true);
      setTimeout(() => setShowCopyToast(false), 3000);
    } catch (err) {
      console.error('Erreur copie:', err);
      setError("Erreur lors de la copie du token");
    }
  };

  // ============================================
  // HELPERS
  // ============================================
  const formatExpirationDate = (dateString) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const isTokenExpired = () => {
    if (!tokenExpiresAt) return false;
    try {
      return new Date(tokenExpiresAt) < new Date();
    } catch {
      return false;
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const hideToken = () => {
    setToken("");
    setNewTokenGenerated(false);
    setShowTokenVisible(false);
  };

  useEffect(() => {
    if (newTokenGenerated) {
      const timer = setTimeout(hideToken, 5 * 60 * 1000);
      return () => clearTimeout(timer);
    }
  }, [newTokenGenerated]);

  // Masquer le token visuellement (avec astérisques)
  const maskToken = (t) => {
    if (!t) return "";
    if (t.length <= 12) return "•".repeat(t.length);
    return t.substring(0, 6) + "••••••••••••••••" + t.substring(t.length - 6);
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <>
      <Header />
      <div className="tk-page main main-app p-3 p-lg-4">
        <style>{TK_STYLES}</style>

        <Row className="justify-content-center">
          <Col lg={9} xl={7}>

            {/* ========== HEADER ========== */}
            <div className="tk-header mb-4">
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                <div className="d-flex align-items-center gap-3">
                  <div className="tk-header-icon">
                    <i className="bi bi-key-fill"></i>
                  </div>
                  <div>
                    <h3 className="tk-header-title mb-1">Token API</h3>
                    <p className="tk-header-subtitle mb-0">
                      Gérez votre token d'accès aux services Minerve Tech
                    </p>
                  </div>
                </div>
                {user?.business_email && (
                  <div className="tk-header-user">
                    <i className="bi bi-envelope-fill"></i>
                    <span>{user.business_email}</span>
                  </div>
                )}
              </div>
            </div>

            {/* ========== CARD PRINCIPALE ========== */}
            <div className="tk-card">
              <div className="tk-card-header">
                <div className="d-flex align-items-center gap-2">
                  <div className="tk-card-header-icon">
                    <i className="bi bi-shield-lock-fill"></i>
                  </div>
                  <div>
                    <h6 className="tk-card-title mb-0">
                      {newTokenGenerated ? "Nouveau token généré" : "Token d'authentification"}
                    </h6>
                    <small className="tk-card-subtitle">
                      {newTokenGenerated
                        ? "Conservez ce token en lieu sûr"
                        : "Générez un token pour accéder à l'API"}
                    </small>
                  </div>
                </div>
                <div className="d-flex gap-2">
                  {isTokenExpired() && (
                    <span className="tk-badge tk-badge-red">
                      <i className="bi bi-exclamation-triangle-fill"></i>
                      Expiré
                    </span>
                  )}
                  {newTokenGenerated && (
                    <span className="tk-badge tk-badge-green">
                      <i className="bi bi-check-circle-fill"></i>
                      Nouveau
                    </span>
                  )}
                </div>
              </div>

              <div className="tk-card-body">
                {loading ? (
                  <div className="tk-loading">
                    <div className="tk-loading-spinner"></div>
                    <div className="tk-loading-text">Chargement...</div>
                  </div>
                ) : (
                  <>
                    {/* ========== TOKEN DISPLAY ========== */}
                    {token ? (
                      <>
                        <div className="tk-token-box">
                          <div className="tk-token-label">
                            <i className="bi bi-key"></i>
                            Votre token API
                          </div>
                          <div className="tk-token-input-wrapper">
                            <div className="tk-token-icon">
                              <i className="bi bi-shield-lock-fill"></i>
                            </div>
                            <input
                              type="text"
                              className="tk-token-input"
                              value={showTokenVisible ? token : maskToken(token)}
                              readOnly
                            />
                            <button
                              className={`tk-token-toggle ${showTokenVisible ? 'active' : ''}`}
                              onClick={() => setShowTokenVisible(!showTokenVisible)}
                              title={showTokenVisible ? "Masquer" : "Afficher"}
                            >
                              <i className={`bi ${showTokenVisible ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
                            </button>
                            <button
                              className="tk-token-copy"
                              onClick={copyToken}
                              title="Copier"
                            >
                              <i className="bi bi-clipboard-fill"></i>
                              <span>Copier</span>
                            </button>
                          </div>
                        </div>

                        <div className="tk-warning-box">
                          <i className="bi bi-exclamation-triangle-fill"></i>
                          <div>
                            <strong>Important</strong>
                            <p>Ce token ne sera affiché qu'une seule fois. Copiez-le maintenant.</p>
                          </div>
                        </div>

                        {tokenExpiresAt && (
                          <div className={`tk-info-inline ${isTokenExpired() ? 'tk-info-danger' : 'tk-info-success'}`}>
                            <i className={`bi ${isTokenExpired() ? 'bi-exclamation-triangle-fill' : 'bi-clock-fill'}`}></i>
                            <span>
                              {isTokenExpired() ? 'Token expiré le ' : 'Expire le '}
                              <strong>{formatExpirationDate(tokenExpiresAt)}</strong>
                            </span>
                          </div>
                        )}

                        <button className="tk-btn-hide" onClick={hideToken}>
                          <i className="bi bi-eye-slash"></i>
                          Masquer le token
                        </button>
                      </>
                    ) : (
                      <div className="tk-empty-state">
                        <div className="tk-empty-icon">
                          <i className="bi bi-shield-lock"></i>
                        </div>
                        <h5 className="tk-empty-title">Aucun token affiché</h5>
                        <p className="tk-empty-text">
                          {newTokenGenerated
                            ? "Le token a été masqué pour des raisons de sécurité."
                            : "Générez un nouveau token pour l'afficher ici."}
                        </p>

                        {tokenExpiresAt && !newTokenGenerated && (
                          <div className={`tk-info-inline ${isTokenExpired() ? 'tk-info-danger' : 'tk-info-success'}`}>
                            <i className={`bi ${isTokenExpired() ? 'bi-exclamation-triangle-fill' : 'bi-clock-fill'}`}></i>
                            <span>
                              {isTokenExpired() ? 'Dernier token expiré le ' : 'Dernier token expirait le '}
                              <strong>{formatExpirationDate(tokenExpiresAt)}</strong>
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* ========== INFO ========== */}
                    <div className="tk-info-box">
                      <i className="bi bi-info-circle-fill"></i>
                      <div>
                        <strong>À quoi sert ce token ?</strong>
                        <p>
                          {token
                            ? "Utilisez ce token pour authentifier vos requêtes API vers l'API SMS."
                            : "Le token n'est affiché qu'après génération pour des raisons de sécurité."}
                        </p>
                      </div>
                    </div>

                    {/* ========== ACTION BUTTON ========== */}
                    <button
                      className="tk-btn-primary tk-btn-large"
                      onClick={() => setShowGenerateModal(true)}
                      disabled={regenerating}
                    >
                      {regenerating ? (
                        <>
                          <Spinner animation="border" size="sm" />
                          Génération en cours...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-arrow-repeat"></i>
                          {token ? "Régénérer le token" : "Générer un nouveau token"}
                        </>
                      )}
                    </button>

                    {/* ========== WARNING ========== */}
                    <div className="tk-alert-warning">
                      <i className="bi bi-exclamation-triangle-fill"></i>
                      <div>
                        <strong>Attention</strong>
                        <p>
                          {token
                            ? "Le token actuel sera immédiatement invalidé. Pensez à copier le nouveau token."
                            : "La génération d'un nouveau token invalidera immédiatement le token actuel."}
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

          </Col>
        </Row>

        {/* ============================================
            MODAL GÉNÉRATION
            ============================================ */}
        <Modal show={showGenerateModal} onHide={() => setShowGenerateModal(false)} centered className="tk-modal">
          <div className="tk-modal-header">
            <div className="d-flex align-items-center gap-3">
              <div className="tk-modal-icon">
                <i className="bi bi-key-fill"></i>
              </div>
              <div>
                <h5 className="tk-modal-title mb-0">Générer un nouveau token</h5>
                <small className="tk-modal-subtitle">Un token sécurisé</small>
              </div>
            </div>
            <button className="tk-modal-close" onClick={() => setShowGenerateModal(false)}>
              <i className="bi bi-x-lg"></i>
            </button>
          </div>

          <Form onSubmit={generateToken}>
            <div className="tk-modal-body">
              <div className="tk-modal-hero">
                <div className="tk-modal-hero-icon">
                  <i className="bi bi-shield-lock-fill"></i>
                </div>
                <h5 className="tk-modal-hero-title">Générer un nouveau token API</h5>
                <p className="tk-modal-hero-text">
                  Un nouveau token sera généré pour l'accès aux services Minerve Tech.
                </p>
              </div>

              {error && (
                <div className="tk-alert tk-alert-danger">
                  <i className="bi bi-exclamation-triangle-fill"></i>
                  <div>{error}</div>
                </div>
              )}

              <div className="tk-form-group">
                <label className="tk-form-label">Email</label>
                <div className="tk-input-wrapper">
                  <i className="bi bi-envelope tk-input-icon"></i>
                  <input
                    type="email"
                    className="tk-input tk-input-readonly"
                    value={user?.business_email || ""}
                    readOnly
                  />
                </div>
                <div className="tk-form-hint">
                  <i className="bi bi-info-circle"></i>
                  Email utilisé pour l'authentification
                </div>
              </div>

              <div className="tk-form-group">
                <label className="tk-form-label">
                  Mot de passe <span className="tk-required">*</span>
                </label>
                <div className="tk-input-wrapper">
                  <i className="bi bi-lock tk-input-icon"></i>
                  <input
                    type={showPassword ? "text" : "password"}
                    className="tk-input tk-input-with-actions"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Saisissez votre mot de passe"
                    required
                    disabled={regenerating}
                  />
                  <button
                    type="button"
                    className="tk-input-action tk-input-toggle"
                    onClick={togglePasswordVisibility}
                    disabled={regenerating}
                    title={showPassword ? "Masquer" : "Afficher"}
                  >
                    <i className={`bi ${showPassword ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
                  </button>
                </div>
                <div className="tk-form-hint">
                  <i className="bi bi-info-circle"></i>
                  Nécessaire pour valider la génération
                </div>
              </div>

              <div className="tk-info-box tk-info-box-small">
                <i className="bi bi-info-circle-fill"></i>
                <div>
                  Le nouveau token sera affiché une seule fois après génération.
                </div>
              </div>
            </div>

            <div className="tk-modal-footer">
              <button
                type="button"
                className="tk-btn-cancel"
                onClick={() => {
                  setShowGenerateModal(false);
                  setPassword("");
                  setError("");
                  setShowPassword(false);
                }}
                disabled={regenerating}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="tk-btn-primary"
                disabled={regenerating || !password}
              >
                {regenerating ? (
                  <>
                    <Spinner animation="border" size="sm" />
                    Génération...
                  </>
                ) : (
                  <>
                    <i className="bi bi-key-fill"></i>
                    Générer le token
                  </>
                )}
              </button>
            </div>
          </Form>
        </Modal>

        {/* ============================================
            TOAST DE COPIE
            ============================================ */}
        <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
          <Toast
            show={showCopyToast}
            onClose={() => setShowCopyToast(false)}
            delay={3000}
            autohide
            bg="success"
          >
            <Toast.Header className="bg-success text-white">
              <i className="bi bi-clipboard-check-fill me-2"></i>
              <strong className="me-auto">Succès</strong>
              <small>Maintenant</small>
            </Toast.Header>
            <Toast.Body className="text-white">
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-check-circle-fill fs-5"></i>
                <div>
                  <strong>Token copié !</strong>
                  <div className="small">Le token a été copié dans le presse-papier</div>
                </div>
              </div>
            </Toast.Body>
          </Toast>
        </ToastContainer>

        <Footer />
      </div>

      {/* ========== STYLES PREMIUM ========== */}
      <style>{TK_STYLES}</style>
    </>
  );
}

// ============================================
// STYLES
// ============================================
const TK_STYLES = `
  /* ========== GLOBAL ========== */
  .tk-page { background: #f8fafc; min-height: 100vh; }
  .tk-page * { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }

  /* ========== HEADER ========== */
  .tk-header {
    background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
    border-radius: 16px; padding: 24px 28px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 3px rgba(0,0,0,0.03);
  }
  .tk-header-icon {
    width: 56px; height: 56px; border-radius: 14px;
    background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
    color: white; display: flex; align-items: center; justify-content: center;
    font-size: 26px;
    box-shadow: 0 8px 20px -6px rgba(59, 130, 246, 0.5);
  }
  .tk-header-title { font-weight: 700; color: #0f172a; font-size: 1.5rem; letter-spacing: -0.02em; }
  .tk-header-subtitle { color: #64748b; font-size: 0.875rem; }
  .tk-header-user {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 8px 14px; background: white;
    border: 1px solid #e2e8f0; border-radius: 10px;
    font-size: 0.82rem; color: #475569; font-weight: 500;
  }
  .tk-header-user > i { color: #3b82f6; font-size: 0.9rem; }

  /* ========== CARD ========== */
  .tk-card {
    background: white; border-radius: 16px;
    border: 1px solid #e2e8f0; overflow: hidden;
    box-shadow: 0 1px 3px rgba(0,0,0,0.03);
  }
  .tk-card-header {
    padding: 20px 24px;
    background: linear-gradient(180deg, #ffffff, #fafbfc);
    border-bottom: 1px solid #f1f5f9;
    display: flex; justify-content: space-between; align-items: center;
    flex-wrap: wrap; gap: 12px;
  }
  .tk-card-header-icon {
    width: 42px; height: 42px; border-radius: 10px;
    background: #eff6ff; color: #3b82f6;
    display: flex; align-items: center; justify-content: center;
    font-size: 20px;
  }
  .tk-card-title { font-weight: 700; color: #0f172a; font-size: 0.95rem; margin: 0; }
  .tk-card-subtitle { color: #94a3b8; font-size: 0.75rem; }
  .tk-card-body { padding: 24px; }

  /* ========== BADGES ========== */
  .tk-badge {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 5px 11px; border-radius: 6px;
    font-size: 0.72rem; font-weight: 700;
  }
  .tk-badge-red { background: #fee2e2; color: #dc2626; }
  .tk-badge-green { background: #d1fae5; color: #059669; }

  /* ========== TOKEN BOX ========== */
  .tk-token-box { margin-bottom: 20px; }
  .tk-token-label {
    display: flex; align-items: center; gap: 8px;
    font-weight: 700; color: #0f172a;
    font-size: 0.82rem; margin-bottom: 10px;
  }
  .tk-token-label > i { color: #3b82f6; }
  .tk-token-input-wrapper {
    position: relative; display: flex; align-items: center;
    background: #f8fafc; border: 2px solid #e2e8f0;
    border-radius: 12px; padding: 4px;
    transition: all 0.2s;
  }
  .tk-token-input-wrapper:hover {
    border-color: #cbd5e1;
    background: white;
  }
  .tk-token-icon {
    width: 40px; height: 40px; border-radius: 8px;
    background: #eff6ff; color: #3b82f6;
    display: flex; align-items: center; justify-content: center;
    font-size: 18px; flex-shrink: 0;
    margin-right: 8px;
  }
  .tk-token-input {
    flex-grow: 1; border: none; outline: none;
    background: transparent;
    font-family: 'SF Mono', Menlo, Monaco, Consolas, monospace;
    font-size: 0.82rem; color: #0f172a;
    font-weight: 600;
    padding: 8px 4px;
    letter-spacing: 0.02em;
    min-width: 0;
  }
  .tk-token-toggle {
    width: 38px; height: 38px; border-radius: 8px;
    background: white; border: 1px solid #e2e8f0;
    color: #64748b; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    font-size: 1rem; transition: all 0.2s;
    margin-right: 4px; flex-shrink: 0;
  }
  .tk-token-toggle:hover { background: #f1f5f9; color: #3b82f6; border-color: #3b82f6; }
  .tk-token-toggle.active { background: #eff6ff; color: #3b82f6; border-color: #3b82f6; }
  .tk-token-copy {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 9px 16px; border-radius: 8px;
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white; border: none; cursor: pointer;
    font-weight: 600; font-size: 0.8rem;
    transition: all 0.2s; flex-shrink: 0;
  }
  .tk-token-copy:hover {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px -2px rgba(59, 130, 246, 0.4);
  }

  /* ========== WARNING BOX ========== */
  .tk-warning-box {
    display: flex; align-items: flex-start; gap: 12px;
    padding: 14px 18px; border-radius: 12px;
    background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
    color: #92400e; border-left: 4px solid #f59e0b;
    margin-bottom: 14px;
  }
  .tk-warning-box > i { color: #f59e0b; font-size: 1.3rem; flex-shrink: 0; margin-top: 2px; }
  .tk-warning-box strong { display: block; font-size: 0.85rem; margin-bottom: 3px; }
  .tk-warning-box p { margin: 0; font-size: 0.82rem; opacity: 0.85; line-height: 1.5; }

  /* ========== INFO INLINE ========== */
  .tk-info-inline {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 10px 14px; border-radius: 10px;
    font-size: 0.8rem; font-weight: 500;
    margin-bottom: 14px;
  }
  .tk-info-success {
    background: #d1fae5; color: #065f46;
  }
  .tk-info-success > i { color: #10b981; }
  .tk-info-danger {
    background: #fee2e2; color: #991b1b;
  }
  .tk-info-danger > i { color: #ef4444; }
  .tk-info-inline strong { font-weight: 700; }

  /* ========== HIDE BUTTON ========== */
  .tk-btn-hide {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 8px 14px; border-radius: 8px;
    background: white; border: 1px solid #e2e8f0;
    color: #64748b; cursor: pointer;
    font-weight: 600; font-size: 0.78rem;
    transition: all 0.2s;
  }
  .tk-btn-hide:hover {
    background: #f1f5f9; color: #334155;
    border-color: #cbd5e1;
  }

  /* ========== EMPTY STATE ========== */
  .tk-empty-state {
    text-align: center; padding: 40px 20px;
    background: #f8fafc; border-radius: 12px;
    border: 1px dashed #cbd5e1; margin-bottom: 20px;
  }
  .tk-empty-icon {
    width: 80px; height: 80px; border-radius: 50%;
    background: #f1f5f9; color: #94a3b8;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 36px; margin-bottom: 20px;
  }
  .tk-empty-title {
    color: #334155; font-weight: 700;
    margin-bottom: 8px; font-size: 1rem;
  }
  .tk-empty-text {
    color: #94a3b8; margin-bottom: 16px; font-size: 0.85rem;
  }

  /* ========== INFO BOX ========== */
  .tk-info-box {
    display: flex; align-items: flex-start; gap: 12px;
    padding: 14px 18px; border-radius: 12px;
    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
    color: #1e40af; border-left: 4px solid #3b82f6;
    margin-bottom: 20px;
  }
  .tk-info-box > i { color: #3b82f6; font-size: 1.3rem; flex-shrink: 0; margin-top: 2px; }
  .tk-info-box strong { display: block; font-size: 0.82rem; margin-bottom: 3px; }
  .tk-info-box p { margin: 0; font-size: 0.8rem; opacity: 0.85; line-height: 1.5; }
  .tk-info-box-small {
    padding: 10px 14px;
    font-size: 0.78rem;
    margin-bottom: 0;
  }
  .tk-info-box-small > i { font-size: 1.1rem; }

  /* ========== BUTTONS ========== */
  .tk-btn-primary {
    display: inline-flex; align-items: center; justify-content: center;
    gap: 8px; padding: 11px 20px;
    border-radius: 10px; font-weight: 600;
    font-size: 0.875rem; border: none;
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white; cursor: pointer;
    transition: all 0.2s;
  }
  .tk-btn-primary:hover:not(:disabled) {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    transform: translateY(-1px);
    box-shadow: 0 8px 16px -4px rgba(59, 130, 246, 0.4);
  }
  .tk-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  .tk-btn-large { padding: 14px 24px; font-size: 0.95rem; width: 100%; }

  .tk-btn-cancel {
    display: inline-flex; align-items: center; justify-content: center;
    gap: 8px; padding: 11px 20px;
    border-radius: 10px; font-weight: 600;
    font-size: 0.875rem;
    background: white; border: 1px solid #e2e8f0;
    color: #334155; cursor: pointer;
    transition: all 0.2s;
  }
  .tk-btn-cancel:hover { background: #f1f5f9; }

  /* ========== ALERT WARNING ========== */
  .tk-alert-warning {
    display: flex; align-items: flex-start; gap: 12px;
    padding: 14px 18px; border-radius: 12px;
    background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
    color: #92400e; border-left: 4px solid #f59e0b;
    margin-top: 20px;
  }
  .tk-alert-warning > i { color: #f59e0b; font-size: 1.3rem; flex-shrink: 0; margin-top: 2px; }
  .tk-alert-warning strong { display: block; font-size: 0.85rem; margin-bottom: 3px; }
  .tk-alert-warning p { margin: 0; font-size: 0.82rem; opacity: 0.85; line-height: 1.5; }

  .tk-alert {
    display: flex; align-items: center; gap: 10px;
    padding: 12px 16px; border-radius: 10px;
    font-size: 0.82rem; font-weight: 500;
    margin-bottom: 16px;
  }
  .tk-alert-danger {
    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
    color: #991b1b; border-left: 4px solid #ef4444;
  }
  .tk-alert-danger > i { color: #ef4444; }

  /* ========== LOADING ========== */
  .tk-loading {
    display: flex; flex-direction: column;
    justify-content: center; align-items: center;
    padding: 60px 20px;
  }
  .tk-loading-spinner {
    width: 48px; height: 48px;
    border: 4px solid #e2e8f0;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: tk-spin 0.8s linear infinite;
    margin-bottom: 16px;
  }
  .tk-loading-text { color: #64748b; font-size: 0.875rem; font-weight: 500; }
  @keyframes tk-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  /* ========== MODAL ========== */
  .tk-modal .modal-content {
    border: none; border-radius: 16px; overflow: hidden;
  }
  .tk-modal-header {
    display: flex; justify-content: space-between; align-items: center;
    padding: 20px 24px;
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
    color: white;
  }
  .tk-modal-icon {
    width: 44px; height: 44px; border-radius: 10px;
    background: rgba(255,255,255,0.1);
    display: flex; align-items: center; justify-content: center;
    font-size: 20px;
  }
  .tk-modal-title { font-weight: 700; letter-spacing: -0.01em; margin: 0; }
  .tk-modal-subtitle { color: #94a3b8; font-size: 0.8rem; }
  .tk-modal-close {
    background: rgba(255,255,255,0.1); border: none; color: white;
    width: 36px; height: 36px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.2s;
  }
  .tk-modal-close:hover { background: rgba(255,255,255,0.2); transform: rotate(90deg); }
  .tk-modal-body { padding: 24px; background: #f8fafc; }
  .tk-modal-footer {
    padding: 16px 24px; background: white;
    border-top: 1px solid #e2e8f0;
    display: flex; justify-content: flex-end; gap: 10px;
  }

  /* ========== MODAL HERO ========== */
  .tk-modal-hero {
    text-align: center; margin-bottom: 24px;
  }
  .tk-modal-hero-icon {
    width: 80px; height: 80px; border-radius: 50%;
    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
    color: #3b82f6;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 36px; margin-bottom: 16px;
    box-shadow: 0 12px 24px -8px rgba(59, 130, 246, 0.3);
  }
  .tk-modal-hero-title {
    font-weight: 700; color: #0f172a;
    font-size: 1.15rem; margin-bottom: 8px;
  }
  .tk-modal-hero-text {
    color: #64748b; font-size: 0.85rem;
    margin: 0;
  }

  /* ========== FORM ========== */
  .tk-form-group { margin-bottom: 18px; }
  .tk-form-label {
    display: block; font-weight: 700;
    color: #0f172a; font-size: 0.82rem;
    margin-bottom: 8px;
  }
  .tk-required { color: #ef4444; }
  .tk-input-wrapper { position: relative; display: flex; align-items: center; }
  .tk-input-icon {
    position: absolute; left: 16px;
    color: #94a3b8; font-size: 1.05rem;
    pointer-events: none; z-index: 5;
  }
  .tk-input {
    width: 100%; padding: 12px 16px 12px 46px;
    border: 2px solid #e2e8f0; border-radius: 10px;
    font-size: 0.875rem; color: #0f172a;
    background: white; transition: all 0.2s;
    outline: none; font-family: inherit;
  }
  .tk-input:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
  }
  .tk-input-readonly {
    background: #f1f5f9 !important;
    color: #64748b !important;
    cursor: not-allowed;
  }
  .tk-input-with-actions { padding-right: 52px; }
  .tk-input-action {
    position: absolute; right: 8px;
    width: 38px; height: 38px;
    border-radius: 8px; background: white;
    border: 1px solid #e2e8f0;
    color: #64748b; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.2s; font-size: 1rem;
  }
  .tk-input-action:hover:not(:disabled) {
    background: #f1f5f9; color: #3b82f6;
    border-color: #3b82f6;
  }
  .tk-input-action:disabled { opacity: 0.5; cursor: not-allowed; }
  .tk-form-hint {
    margin-top: 6px; font-size: 0.72rem;
    color: #94a3b8; display: flex;
    align-items: center; gap: 4px;
  }
  .tk-form-hint i { color: #3b82f6; }

  /* ========== RESPONSIVE ========== */
  @media (max-width: 768px) {
    .tk-header { padding: 20px; }
    .tk-header-title { font-size: 1.2rem; }
    .tk-card-body { padding: 16px; }
    .tk-token-input { font-size: 0.72rem; }
    .tk-token-copy span { display: none; }
    .tk-token-copy { padding: 9px 12px; }
    .tk-modal-body { padding: 16px; }
    .tk-modal-footer { flex-direction: column; }
    .tk-modal-footer > * { width: 100%; }
  }
`;